import type { Stop, StopType } from '#domain/model/Stop.ts';
import type { Coordinates } from '#features/livemonitor/model/Coordinates.ts';
import type { MapMarker } from '#features/livemonitor/model/MapMarker.ts';
import type { MapSegment } from '#features/livemonitor/model/MapSegment.ts';
import { truncate } from '#utils/stringUtils.ts';

const MIN_BOUNDING_BOX_PADDING = 0.01;
const BOUNDING_BOX_PADDING_FACTOR = 0.1;

const successColor = 'bg-map-marker-success';
const routeColor = 'bg-map-marker-route';

export const mapStopsToRoute = (stops: Stop[]) => {
    const markers: MapMarker[] = stops.flatMap((stop, index) => {
        const position = getStopPosition(stop);

        if (!position) {
            return [];
        }

        return [
            {
                id: stop.id,
                markerProps: {
                    active: false,
                    iconNames: [getMarkerIcon(index, stops.length, stop.type)],
                    markerColor: stop.status === 'finished' ? successColor : routeColor,
                    name: `${index + 1} - ${truncate(stop.name, 20)}`,
                },
                position,
            },
        ];
    });

    const segments: MapSegment[] = [];
    markers.forEach((marker, index) => {
        if (index === markers.length - 1) {
            return;
        }
        const nextMarker = markers[index + 1];
        const isNextStopFinished = stops[index + 1].status === 'finished';

        const segment: MapSegment = {
            data: {},
            points: [
                {
                    lat: marker.position.lat,
                    lng: marker.position.lng,
                    pointIndex: 2 * index,
                },
                {
                    lat: nextMarker.position.lat,
                    lng: nextMarker.position.lng,
                    pointIndex: 2 * index + 1,
                },
            ],
            style: isNextStopFinished ? { color: '#5cb85c' } : {},
            showArrows: true,
            reduced: false,
            alternative: false,
        };

        segments.push(segment);
    });

    const bbox = getBoundingBox(markers.map(({ position }) => position));

    return {
        markers,
        segments,
        bbox,
    };
};

const getMarkerIcon = (index: number, stopsLength: number, type: StopType) => {
    if (index === stopsLength - 1) {
        return 'finish';
    }
    if (index === 0) {
        return 'start';
    }
    switch (type) {
        case 'load':
            return 'arrow-down';
        case 'unload':
            return 'arrow-up';
        case 'service':
            return 'wrench';
        case 'unknown':
            return 'route';
    }
};

const getBoundingBox = (positions: Array<{ lat: number; lng: number }>) => {
    if (positions.length === 0) {
        return null;
    }

    const lats = positions.map(({ lat }) => lat);
    const lngs = positions.map(({ lng }) => lng);
    const latPadding = getBoundingBoxPadding(Math.max(...lats) - Math.min(...lats));
    const lngPadding = getBoundingBoxPadding(Math.max(...lngs) - Math.min(...lngs));

    return {
        topLeft: {
            lat: Math.max(...lats) + latPadding,
            lng: Math.min(...lngs) - lngPadding,
        },
        bottomRight: {
            lat: Math.min(...lats) - latPadding,
            lng: Math.max(...lngs) + lngPadding,
        },
    };
};

const getBoundingBoxPadding = (span: number) => Math.max(span * BOUNDING_BOX_PADDING_FACTOR, MIN_BOUNDING_BOX_PADDING);

const getStopPosition = (stop: Stop): Coordinates | undefined => {
    const lat = parseCoordinate(stop.location.latitude);
    const lng = parseCoordinate(stop.location.longitude);

    if (lat === undefined || lng === undefined) {
        return;
    }

    return { lat, lng };
};

const parseCoordinate = (value?: string) => {
    if (!value) {
        return;
    }

    const coordinate = Number(value);

    return Number.isFinite(coordinate) ? coordinate : undefined;
};
